import { useEffect, useState } from "react";
import axios from "axios";

/* Site Builder settings , shared by the public shell (nav filtering + theme
   vars + typography + section order) and the admin Site Builder page.
   Supports an unsaved DRAFT preview: the Site Builder writes the draft to
   localStorage and opens the site with ?sitedraft=1; that tab then renders
   the draft instead of the published settings. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _cache = null;
let _promise = null;

function draftActive() {
  try {
    if (new URLSearchParams(window.location.search).has("sitedraft")) {
      sessionStorage.setItem("hd-sitedraft", "1");
    }
    return sessionStorage.getItem("hd-sitedraft") === "1";
  } catch { return false; }
}

function readDraft() {
  try { return JSON.parse(localStorage.getItem("heritage-site-draft") || "null"); }
  catch { return null; }
}

export function fetchSiteSettings(force = false) {
  if (draftActive()) {
    const d = readDraft();
    if (d) return Promise.resolve(d);
  }
  if (_cache && !force) return Promise.resolve(_cache);
  if (_promise && !force) return _promise;
  _promise = axios.get(`${API}/site-settings`)
    .then((r) => { _cache = r.data || {}; return _cache; })
    .catch(() => ({}));
  return _promise;
}

export function invalidateSiteSettings() { _cache = null; _promise = null; }

export function useSiteSettings() {
  const [settings, setSettings] = useState(draftActive() ? readDraft() : _cache);
  useEffect(() => { fetchSiteSettings().then(setSettings); }, []);
  return settings;
}

const FONT_STACKS = {
  "Cormorant Garamond": "'Cormorant Garamond', Georgia, serif",
  "Playfair Display": "'Playfair Display', Georgia, serif",
  "EB Garamond": "'EB Garamond', Georgia, serif",
  "Lora": "'Lora', Georgia, serif",
  "Merriweather": "'Merriweather', Georgia, serif",
  "Crimson Text": "'Crimson Text', Georgia, serif",
  "Quicksand": "'Quicksand', 'Segoe UI', sans-serif",
  "Nunito": "'Nunito', 'Segoe UI', sans-serif",
  "Georgia": "Georgia, 'Times New Roman', serif",
};
export const FONT_CHOICES = Object.keys(FONT_STACKS);

function ensureFontLoaded(name) {
  if (!name || name === "Georgia" || !FONT_STACKS[name]) return;
  const id = `site-font-${name.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

const BTN_RADIUS = { pill: "999px", rounded: "8px", sharp: "0px" };
const BTN_SCALE = { sm: "0.9", md: "1", lg: "1.12" };

/* Apply theme + typography + button overrides to :root while public pages mounted. */
export function useSiteTheme(settings) {
  useEffect(() => {
    const root = document.documentElement.style;
    const applied = [];
    const set = (k, v) => { if (v) { root.setProperty(k, v); applied.push(k); } };

    const theme = settings?.theme || {};
    Object.keys(theme).filter((k) => theme[k]).forEach((k) => set(`--${k}`, theme[k]));

    const typo = settings?.typography || {};
    if (typo.heading_font) { ensureFontLoaded(typo.heading_font); set("--site-heading-font", FONT_STACKS[typo.heading_font] || typo.heading_font); }
    if (typo.body_font) { ensureFontLoaded(typo.body_font); set("--site-body-font", FONT_STACKS[typo.body_font] || typo.body_font); }
    if (typo.base_size) set("--site-base-size", `${typo.base_size}px`);
    if (typo.heading_weight) set("--site-heading-weight", typo.heading_weight);

    const btn = settings?.buttons || {};
    if (btn.shape) set("--site-btn-radius", BTN_RADIUS[btn.shape] || "8px");
    if (btn.size) set("--site-btn-scale", BTN_SCALE[btn.size] || "1");

    return () => applied.forEach((k) => root.removeProperty(k));
  }, [settings]);
}
